import React, { useEffect, useState } from 'react';
import { db, functions } from '../../utils/firebase';
import { collection, query, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Order } from '../../types';
import { Package, Truck, CheckCircle, Clock, XCircle, ChevronDown, ChevronUp, Search, Filter } from 'lucide-react';

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const orderList: Order[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Order));
        setOrders(orderList);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleStatusUpdate = async (orderId: string, newStatus: Order['status']) => {
    try {
      const updateStatus = httpsCallable(functions, 'updateOrderStatus');
      await updateStatus({ orderId, status: newStatus });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'processing': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'shipped': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'delivered': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'refunded': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesSearch = 
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerDetails?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerDetails?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    // Handle Firestore Timestamp
    if (date.toDate) return date.toDate().toLocaleDateString();
    // Handle standard Date or string
    return new Date(date).toLocaleDateString();
  };

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full"></div></div>;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-heading tracking-tight">ORDER MANAGEMENT</h2>
        <div className="flex gap-4">
             {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input 
                    type="text" 
                    placeholder="Search Order ID or Email..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black font-body text-sm w-64"
                />
            </div>
            {/* Filter */}
            <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                 <select 
                    value={statusFilter} 
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black font-body text-sm appearance-none bg-white cursor-pointer"
                >
                    <option value="all">All Status</option>
                    <option value="paid">Paid</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="refunded">Refunded</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
            </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 shadow-sm rounded-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Order ID</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Date</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Customer</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Total</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest">Status</th>
              <th className="p-4 font-heading text-xs text-gray-500 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredOrders.map(order => (
              <React.Fragment key={order.id}>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="p-4 font-mono text-sm font-bold text-gray-700">#{order.id.slice(0, 8)}...</td>
                  <td className="p-4 text-sm text-gray-600">{formatDate(order.createdAt)}</td>
                  <td className="p-4">
                    <div className="flex flex-col">
                        <span className="font-bold text-sm">{order.customerDetails?.name || 'Guest'}</span>
                        <span className="text-xs text-gray-500">{order.customerDetails?.email}</span>
                    </div>
                  </td>
                  <td className="p-4 font-bold text-sm">£{order.totalAmount?.toFixed(2)}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getStatusColor(order.status)} uppercase tracking-wide`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button 
                        onClick={() => setExpandedId(expandedId === order.id ? null : order.id)}
                        className="text-gray-400 hover:text-black transition-colors"
                    >
                        {expandedId === order.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                  </td>
                </tr>
                {expandedId === order.id && (
                  <tr>
                    <td colSpan={6} className="bg-gray-50 p-6 border-b border-gray-100 inset-shadow">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {/* Items */}
                            <div className="md:col-span-2">
                                <h4 className="font-heading text-xs font-bold text-gray-500 uppercase mb-4 tracking-widest">Order Items</h4>
                                <div className="space-y-4">
                                    {order.items.map((item: any, idx) => (
                                        <div key={idx} className="flex gap-4 bg-white p-3 rounded border border-gray-200">
                                            <img src={item.image} alt={item.title} className="w-12 h-12 object-cover rounded bg-gray-100" />
                                            <div className="flex-1">
                                                <div className="flex justify-between">
                                                    <h5 className="font-bold text-sm">{item.title}</h5>
                                                    <span className="text-sm font-mono">£{item.price.toFixed(2)}</span>
                                                </div>
                                                {/* Everything needed to re-key a Printful / AS Colour / deck order */}
                                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                                                    <span>Qty: {item.quantity}</span>
                                                    {item.selectedSize && <span>Size: {item.selectedSize}</span>}
                                                    {item.selectedColor && <span>Color: {item.selectedColor}</span>}
                                                    {item.tier && <span className="uppercase">{item.tier}</span>}
                                                    {item.editionNumbers && item.editionNumbers.length > 0 && (
                                                        <span className="font-mono text-gray-700 font-bold">
                                                            {item.editionNumbers.map((n: number) => `No. ${n}`).join(', ')}
                                                        </span>
                                                    )}
                                                </div>
                                                {item.artifactConflict && (
                                                    <p className="mt-2 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                                                        ARTIFACT CONFLICT — this artifact was claimed by an earlier order. Refund this line via the Stripe dashboard (see REFUNDS.md).
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Details & Controls */}
                            <div className="space-y-6">
                                <div>
                                    <h4 className="font-heading text-xs font-bold text-gray-500 uppercase mb-2 tracking-widest">Update Status</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {(['processing', 'shipped', 'delivered', 'cancelled', 'refunded'] as const).map(status => (
                                            <button
                                                key={status}
                                                onClick={() => handleStatusUpdate(order.id, status)}
                                                disabled={order.status === status}
                                                className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-sm border transition-colors
                                                    ${order.status === status 
                                                        ? 'bg-black text-white border-black opacity-50 cursor-not-allowed' 
                                                        : 'bg-white text-gray-600 border-gray-300 hover:border-black hover:text-black'
                                                    }`}
                                            >
                                                {status}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-heading text-xs font-bold text-gray-500 uppercase mb-2 tracking-widest">Shipping Address</h4>
                                    {order.shippingDetails?.address ? (
                                        <div className="text-sm text-gray-600 bg-white p-3 rounded border border-gray-200">
                                            <p className="font-bold text-gray-900 mb-1">{order.shippingDetails.name}</p>
                                            <p>{order.shippingDetails.address.line1}</p>
                                            {order.shippingDetails.address.line2 && <p>{order.shippingDetails.address.line2}</p>}
                                            <p>
                                                {order.shippingDetails.address.city}, {order.shippingDetails.address.state} {order.shippingDetails.address.postal_code}
                                            </p>
                                            <p>{order.shippingDetails.address.country}</p>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400 italic">No shipping details provided</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {filteredOrders.length === 0 && (
                <tr>
                    <td colSpan={6} className="p-12 text-center text-gray-500">
                        {orders.length === 0 ? "No orders found." : "No orders match your filter."}
                    </td>
                </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminOrders;
